/** @file ArtScreenFrame: mounts the Art panel and portals the turntable viewport into its turntable slot. */
import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { panelRegistry } from '../shell/panelRegistry';

/**
 * The Art panel renders a `<div data-panel="turntable">`; once it is in the DOM the frame renders
 * `Viewport mode="turntable"` into it (a portal; a MutationObserver notices the slot appearing or going away), so the
 * Art panel never imports the viewport.
 */
export function ArtScreenFrame(): ReactElement {
  const { artSlots: ArtSlots, viewport: Viewport } = panelRegistry;
  const frame = useRef<HTMLDivElement>(null);
  const [slot, setSlot] = useState<Element | null>(null);
  useLayoutEffect(() => {
    const root = frame.current;
    if (root === null) return;
    const find = (): void => {
      const found = root.querySelector('[data-panel="turntable"]');
      setSlot((prev) => (prev === found ? prev : found));
    };
    find();
    const watch = new MutationObserver(find);
    watch.observe(root, { childList: true, subtree: true });
    return () => { watch.disconnect(); };
  }, []);
  return (
    <div ref={frame} className="p3-panel" data-panel="artSlots">
      <ArtSlots />
      {slot === null ? null : createPortal(<Viewport mode="turntable" />, slot)}
    </div>
  );
}
